import React, { useState, useEffect } from 'lib/react';
import {
  EVGrid, EVTypography, EVCircularProgress,
  makeStyles,
} from 'lib/components';
import { useTranslation } from 'lib/translation';

const useStyles = makeStyles((theme) => ({
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    height: '100%',
    gap: '24px',
  },
  title: {
    color: theme.evPalette.charcoal[900],
    fontFamily: 'Noto Sans',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '36px',
    textAlign: 'center',
  },
  description: {
    color: theme.evPalette.charcoal[700],
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    textAlign: 'center',
    maxWidth: '400px',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  progress: {
    color: theme.evPalette.ocean[500],
  },
  progressText: {
    color: theme.evPalette.charcoal[600],
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
  countdownText: {
    color: theme.evPalette.ocean[600],
    fontFamily: 'Noto Sans',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    marginTop: '16px',
  },
}));

function PropertyDataLoader() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <EVGrid className={classes.loaderContainer}>
      <EVTypography className={classes.title}>
        {t('order_property_data_loading_title', 'Loading Property Data')}
      </EVTypography>
      <EVTypography className={classes.description}>
        {t('order_property_data_loading_description', 'We are gathering comprehensive property information for you. This may take a few moments.')}
      </EVTypography>

      <EVGrid className={classes.progressContainer}>
        <EVCircularProgress
          className={classes.progress}
          size={48}
          thickness={4}
        />
        <EVTypography className={classes.progressText}>
          {t('order_property_data_loading_progress', 'Retrieving property information...')}
        </EVTypography>
        <EVTypography className={classes.countdownText}>
          {countdown > 0
            ? t('order_property_data_loading_countdown', 'Estimated time remaining: {{time}}', { time: formatTime(countdown) })
            : t('order_property_data_loading_countdown_complete', 'Hold for few more seconds as the API is taking little longer…')}
        </EVTypography>
      </EVGrid>
    </EVGrid>
  );
}

export default PropertyDataLoader;
